import React, { Component, ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary to catch React errors and show a user-friendly fallback
 * instead of a blank screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Quiz Error Boundary caught an error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(206deg, #E1F6FD 15%, #FFF 100%)",
            p: 3,
          }}
        >
          <Box
            sx={{
              maxWidth: 500,
              textAlign: "center",
              background: "#fff",
              borderRadius: "24px",
              p: { xs: 3, md: 5 },
              boxShadow: "0 20px 60px rgba(0,0,0,0.10)",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 28, md: 36 },
                fontWeight: 800,
                color: "#D14C3F",
                mb: 2,
                fontFamily: "Prompt",
              }}
            >
              Oops! Something went wrong
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: "#4A301E",
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              We encountered an unexpected error. Don't worry, your progress has been saved.
              Please refresh the page to continue.
            </Typography>

            <Button
              onClick={this.handleReload}
              sx={{
                background: "#FF7874",
                color: "#fff",
                px: 4,
                py: 1.5,
                borderRadius: "999px",
                fontWeight: 700,
                fontSize: 16,
                textTransform: "none",
                "&:hover": { background: "#E96A67" },
              }}
            >
              Refresh Page
            </Button>

            <Typography
              sx={{
                fontSize: { xs: 12, md: 14 },
                color: "#7B5A4A",
                mt: 3,
                lineHeight: 1.5,
              }}
            >
              If the problem persists, please contact us at{" "}
              <Box
                component="a"
                href="mailto:Support@pawsonplates.com"
                sx={{
                  color: "#D14C3F",
                  fontWeight: 600,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Support@pawsonplates.com
              </Box>
            </Typography>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  background: "#FFF0F0",
                  borderRadius: "12px",
                  textAlign: "left",
                  fontSize: 12,
                  fontFamily: "monospace",
                  color: "#B20000",
                  wordBreak: "break-all",
                }}
              >
                <strong>Error:</strong> {this.state.error.message}
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}
